import React, { useState, useEffect } from 'react';
import { Chart as ChartJS } from 'chart.js/auto';
import { Bar, Pie, Line } from 'react-chartjs-2';
import { fetchSurveyAnalytics } from '../utils/serverComm';
import './SurveyAnalytics.css';

function SurveyAnalytics({ surveyId }) {
  const [surveyData, setSurveyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [chartType, setChartType] = useState('bar');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await fetchSurveyAnalytics(surveyId);
        setSurveyData(data);
        setSelectedQuestion(data.questions[0]?.id);
      } catch (error) {
        console.error('Error fetching survey data:', error);
        setError('Failed to load survey analytics');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [surveyId]);

  const getChartData = () => {
    if (!surveyData || !selectedQuestion) return null;

    const question = surveyData.questions.find(q => q.id === selectedQuestion);
    if (!question) return null;

    const labels = Object.keys(question.responses);
    const data = Object.values(question.responses);

    return {
      labels,
      datasets: [{
        label: question.text,
        data,
        backgroundColor: [
          'rgba(255, 99, 132, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(153, 102, 255, 0.6)',
        ],
        borderWidth: 1
      }]
    };
  };

  const renderChart = () => {
    const chartData = getChartData();
    if (!chartData) return null;

    const chartProps = {
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom'
          }
        }
      }
    };

    switch (chartType) {
      case 'pie':
        return <Pie {...chartProps} />;
      case 'line':
        return <Line {...chartProps} />;
      default:
        return <Bar {...chartProps} />;
    }
  };

  if (loading) return <div className="text-center p-4">Loading analytics...</div>;
  if (error) return <div className="text-center p-4 text-red-600">{error}</div>;
  if (!surveyData) return <div className="text-center p-4">No data available</div>;

  return (
    <div className="survey-analytics">
      <div className="analytics-controls">
        <select
          value={selectedQuestion}
          onChange={(e) => setSelectedQuestion(e.target.value)}
        >
          {surveyData.questions.map(question => (
            <option key={question.id} value={question.id}>
              {question.text}
            </option>
          ))}
        </select>

        <select
          value={chartType}
          onChange={(e) => setChartType(e.target.value)}
        >
          <option value="bar">Bar Chart</option>
          <option value="pie">Pie Chart</option>
          <option value="line">Line Chart</option>
        </select>
      </div>

      <div className="chart-container">
        {renderChart()}
      </div>

      <div className="analytics-summary">
        <h3>Summary</h3>
        <p>Total Responses: {surveyData.totalResponses}</p>
        {/* Add more summary statistics as needed */}
      </div>
    </div>
  );
}

export default SurveyAnalytics; 